import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Database, Plus, Trash2 } from "lucide-react";
import {
  AvailableIntegration,
  Integration,
} from "@/api/IntegrationsAPI";

interface AccountingSystemCardProps {
  onAddIntegration: () => void;
  onDeleteIntegration?: () => void;
  currentIntegration?: Integration;
  availableIntegrations?: AvailableIntegration[];
}

const AccountingSystemCard: React.FC<AccountingSystemCardProps> = ({
  onAddIntegration,
  onDeleteIntegration,
  currentIntegration,
  availableIntegrations = [],
}) => {
  const [matchingIntegration, setMatchingIntegration] =
    useState<AvailableIntegration | undefined>(undefined);

  useEffect(() => {
    if (currentIntegration) {
      const match = availableIntegrations.find(
        (integration) => integration.id === currentIntegration.integrationId
      );
      setMatchingIntegration(match);
    } else {
      setMatchingIntegration(undefined);
    }
  }, [currentIntegration, availableIntegrations]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Database className="mr-2 h-5 w-5" />
          Accounting System
        </CardTitle>
        <CardDescription>
          Integrate with external accounting systems to synchronize financial data
        </CardDescription>
      </CardHeader>
      <CardContent>
        {currentIntegration ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {matchingIntegration && (
                  <img
                    src={matchingIntegration.logoIcon}
                    alt={`${matchingIntegration.name} logo`}
                    className="h-8 w-8 rounded"
                  />
                )}
                <div>
                  <h3 className="font-medium">{currentIntegration.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Connected accounting system
                  </p>
                </div>
              </div>
              {onDeleteIntegration && (
                <Button variant="destructive" onClick={onDeleteIntegration}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Integration
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 rounded-md bg-muted p-4">
              <div>
                <p className="text-sm font-medium">Integration ID</p>
                <p className="text-sm text-muted-foreground">
                  {currentIntegration.id}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Accounting Integration</h3>
              <p className="text-sm text-muted-foreground">
                Connect to your accounting software to sync financial data
              </p>
            </div>
            <Button onClick={onAddIntegration}>
              <Plus className="mr-2 h-4 w-4" />
              Add Integration
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountingSystemCard;
